import { useState, useEffect, type FormEvent } from 'react'
import { Eye, EyeOff, LockKeyhole, AlertCircle, ArrowLeft, ShieldCheck } from 'lucide-react'
import { useNavigate, useLocation, Link } from 'react-router-dom'

import { dashboardRoutes } from '../../constants/routes'
import { apiAdminLogin } from '../../api/auth/adminAuthApi'
import { isAdminAuthenticated } from '../../auth/adminAuth'

const AdminLoginPage = () => {
  const navigate = useNavigate()
  const location = useLocation()

  const [identifier, setIdentifier] = useState('')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [warning, setWarning] = useState<string | null>(null)

  // Redirect if already authenticated
  useEffect(() => {
    if (isAdminAuthenticated()) {
      navigate(dashboardRoutes.overview, { replace: true })
    }
  }, [navigate])

  const destination =
    (location.state as { from?: { pathname?: string } })?.from?.pathname ||
    dashboardRoutes.overview

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setError(null)
    setWarning(null)

    if (!identifier.trim()) {
      setError('Please enter your email or admin username.')
      return
    }

    if (!password) {
      setError('Please enter your password.')
      return
    }

    setLoading(true)
    try {
      const res = await apiAdminLogin(identifier, password)
      if (res.success) {
        navigate(destination, { replace: true })
      } else {
        setError(res.message || 'Authentication failed.')
        setWarning(res.warning || null)
      }
    } catch (err) {
      setError('Connection error. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  return (
    <main className="flex min-h-screen flex-col items-center justify-center bg-[#f7f7f7] px-5 py-10 font-host">
      {/* Return to website link */}
      <div className="mb-6 w-full max-w-[440px]">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-[13px] font-medium text-gray-500 transition hover:text-[#176448]"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Salzburg Cab Homepage
        </Link>
      </div>

      <section className="relative w-full max-w-[440px] overflow-hidden rounded-[24px] bg-gradient-to-b from-[#2b966a] via-[#176448] to-[#111816] px-[36px] pb-[40px] pt-[48px] shadow-[0_20px_50px_rgba(0,0,0,0.18)] sm:px-[42px]">
        {/* Top badge */}
        <div className="mx-auto mb-4 flex h-11 w-11 items-center justify-center rounded-2xl bg-white/10 backdrop-blur-md">
          <ShieldCheck className="h-6 w-6 text-white" />
        </div>

        {/* HEADING */}
        <div className="text-center">
          <h1 className="text-[24px] font-bold leading-tight text-white">
            Welcome Back, Admin
          </h1>

          <p className="mx-auto mt-2 max-w-[280px] text-[13px] font-normal leading-[19px] text-white/80">
            Sign in to access the Salzburg Cab
            <br />
            fleet & reservation management dashboard.
          </p>
        </div>

        {/* ERROR / WARNING ALERT */}
        {error && (
          <div className="mt-5 flex items-start gap-2.5 rounded-xl border border-red-200/40 bg-red-950/50 p-3.5 text-red-100 backdrop-blur-sm animate-in fade-in zoom-in-95 duration-200">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-red-400" />
            <div className="text-[12px] leading-relaxed">
              <p className="font-semibold text-red-200">{error}</p>
              {warning && <p className="mt-1 text-white/80">{warning}</p>}
            </div>
          </div>
        )}

        {/* LOGIN FORM */}
        <form onSubmit={handleSubmit} className="mt-6">
          {/* EMAIL */}
          <div>
            <label
              htmlFor="admin-identifier"
              className="mb-2 block text-[13px] font-medium text-white"
            >
              Email Address / Admin Username
            </label>

            <input
              id="admin-identifier"
              type="text"
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              placeholder="admin@salzburgcab.com"
              autoComplete="username"
              disabled={loading}
              className="h-[50px] w-full rounded-[12px] border border-white/30 bg-white px-[18px] text-[14px] text-[#1e1e1e] outline-none transition placeholder:text-[#8a8a8a] focus:border-[#ff6b27] focus:ring-2 focus:ring-[#ff6b27]/30 disabled:opacity-60"
            />
          </div>

          {/* PASSWORD */}
          <div className="mt-[18px]">
            <label
              htmlFor="admin-password"
              className="mb-2 block text-[13px] font-medium text-white"
            >
              Password
            </label>

            <div className="relative">
              <LockKeyhole
                aria-hidden
                className="pointer-events-none absolute left-[18px] top-1/2 h-[18px] w-[18px] -translate-y-1/2 text-[#777]"
                strokeWidth={1.8}
              />

              <input
                id="admin-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="••••••••"
                autoComplete="current-password"
                disabled={loading}
                className="h-[50px] w-full rounded-[12px] border border-white/30 bg-white pl-[48px] pr-[48px] text-[14px] text-[#1e1e1e] outline-none transition placeholder:text-[#8a8a8a] focus:border-[#ff6b27] focus:ring-2 focus:ring-[#ff6b27]/30 disabled:opacity-60"
              />

              {/* SHOW / HIDE PASSWORD */}
              <button
                type="button"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={() => setShowPassword((visible) => !visible)}
                className="absolute right-[14px] top-1/2 flex h-8 w-8 -translate-y-1/2 items-center justify-center rounded-full text-[#9a9a9a] transition hover:bg-black/5 hover:text-[#444]"
              >
                {showPassword ? (
                  <EyeOff className="h-[18px] w-[18px]" strokeWidth={2} />
                ) : (
                  <Eye className="h-[18px] w-[18px]" strokeWidth={2} />
                )}
              </button>
            </div>
          </div>

          {/* SIGN IN BUTTON */}
          <button
            type="submit"
            disabled={loading}
            className="mt-[24px] flex h-[50px] w-full items-center justify-center rounded-[12px] bg-[#ff6b27] text-[15px] font-semibold text-white shadow-md transition hover:bg-[#f26020] active:scale-[0.99] disabled:opacity-60"
          >
            {loading ? 'Signing In...' : 'Sign In to Dashboard'}
          </button>
        </form>

      </section>
    </main>
  )
}

export default AdminLoginPage